import FunnelLogo from '@/components/v1/FunnelLogo'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface Props {
  onBack?: () => void
  /** The section the current step belongs to. */
  section: string
  /** How far through that section the current step sits, 0–1. */
  sectionProgress: number
  sections: readonly string[]
  withLogin?: boolean
}

export default function FunnelHeader({
  onBack,
  section,
  sectionProgress,
  sections,
  withLogin = false,
}: Props) {
  const { t } = useTranslation()

  const activeSectionIndex = sections.indexOf(section)

  return (
    <header className="bg-hw-cream/90 sticky top-0 z-20 backdrop-blur">
      <div className="hw-container pt-3 pb-4">
        <div className="relative flex h-10 items-center justify-center">
          {onBack && (
            <button
              aria-label={t('pages.onboarding.v1.back')}
              className="text-hw-ink absolute left-0 flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
              onClick={onBack}
              type="button"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
          )}

          <FunnelLogo />

          {withLogin && !('ReactNativeWebView' in window) && (
            <Link
              className="text-hw-primary absolute right-0 text-sm font-semibold hover:opacity-80"
              data-testid="button-login"
              to="/account/sign-in"
            >
              {t('shared.signIn')}
            </Link>
          )}
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-1.5">
            {sections.map((funnelSection, index) => {
              const fill =
                index < activeSectionIndex
                  ? 1
                  : index === activeSectionIndex
                    ? sectionProgress
                    : 0

              return (
                <div
                  className="bg-hw-line h-1.5 flex-1 overflow-hidden rounded-full"
                  key={funnelSection}
                >
                  <div
                    className="bg-hw-primary h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{ width: `${fill * 100}%` }}
                  />
                </div>
              )
            })}
          </div>

          <div className="flex items-baseline justify-between">
            <span className="text-hw-ink text-[11px] font-semibold tracking-[0.08em] uppercase">
              {t(`pages.onboarding.v1.sections.${section}`)}
            </span>

            <span className="text-hw-muted text-[11px] font-semibold tabular-nums">
              {activeSectionIndex + 1}/{sections.length}
            </span>
          </div>
        </div>
      </div>
    </header>
  )
}
